import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { filter, Subject, takeUntil, tap, map } from 'rxjs';
import { GridApplicationTemplateRowResponse } from './models/grid-application-template-row-response';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import { nameOf } from '../../../../../core/functions/string-helpers';
import { GridHelper } from '../../../../../core/models/grid/grid-helper';
import { GridToolbarComponent } from '../../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { GridToolbarOptions } from '../../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { ApplicationTemplateService } from '../../../shared/services/application-template/application-template.service';
import { GridApplicationTemplateRowData } from './models/grid-application-template-row-data';
import { ApplicationTemplateEditCellRendererComponent } from '../../../../../shared/components/grid/cell-renderers/application-template-dashboard/application-template-edit-cell-renderer/application-template-edit-cell-renderer.component';
import { ApplicationTemplateGridService } from '../../shared/services/application-template-grid/application-template-grid.service';
import { ApplicationTemplateFormDialogService } from '../../shared/services/application-template-form-dialog/application-template-form-dialog.service';
import { RouteConstants } from '../../../../../core/constants/routes';

@Component({
    selector: 'app-application-template-dashboard',
    templateUrl: './application-template-dashboard.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AgGridAngular, GridToolbarComponent],
})
export class ApplicationTemplateDashboardComponent implements OnDestroy {
    private destroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly applicationTemplateService: ApplicationTemplateService,
        private readonly applicationTemplateGridService: ApplicationTemplateGridService,
        private readonly applicationTemplateFormDialogService: ApplicationTemplateFormDialogService,
    ) {}

    context: unknown;
    rowData: GridApplicationTemplateRowData[] = [];
    gridApi: GridApi | undefined;

    columnDefs: ColDef[] = [
        {
            field: nameOf<GridApplicationTemplateRowResponse>('name'),
            minWidth: 300,
        },
        {
            field: nameOf<GridApplicationTemplateRowResponse>('description'),
            minWidth: 300,
        },
        {
            headerName: 'Created',
            field: nameOf<GridApplicationTemplateRowResponse>('createdAt'),
            ...GridHelper.defaultDateColDef,
            filter: false,
            suppressSizeToFit: true,
        },
        {
            headerName: 'Modified',
            field: nameOf<GridApplicationTemplateRowResponse>('updatedAt'),
            ...GridHelper.defaultDateColDef,
            filter: false,
            sort: 'desc',
            suppressSizeToFit: true,
        },
        {
            headerName: 'Action',
            filter: false,
            sortable: false,
            width: 100,
            cellRenderer: ApplicationTemplateEditCellRendererComponent,
            cellRendererParams: {
                returnRoute: RouteConstants.Template.getFullRoute('INDEX'),
            },
        },
    ];
    gridToolbarOptions: GridToolbarOptions = {
        ...GridHelper.defaultToolbarOptions,
        exportCsv: false,
        primaryGridAction: {
            title: 'New Template',
            action: () => {
                this.applicationTemplateFormDialogService.displayCreationFormDialog();
            },
        },
    };

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
    };

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onGridReady(params: GridReadyEvent<unknown>) {
        this.gridApi = params.api;

        this.loadGridData();
    }

    private loadGridData() {
        this.applicationTemplateService
            .getAll()
            .pipe(
                takeUntil(this.destroy$),
                map(
                    (
                        applicationTemplates: GridApplicationTemplateRowResponse[],
                    ) =>
                        this.applicationTemplateGridService.transformRowData(
                            applicationTemplates,
                        ),
                ),
                tap((rowData) => {
                    this.rowData = rowData;
                }),
                filter(() => !!this.gridApi),
            )
            .subscribe(() => {
                this.gridApi!.setGridOption('rowData', this.rowData);
                this.gridApi!.setGridOption('columnDefs', this.columnDefs);
            });
    }
}
